import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ConsoleShell, useConsoleTheme } from '../components/Console';
import { supabase } from '../supabaseClient';

const F = 'system-ui,-apple-system,"Helvetica Neue",Arial,sans-serif';
const USER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

// ── Brand / env config ────────────────────────────────────────────────────────
const FROM_EMAIL      = process.env.REACT_APP_FROM_EMAIL      || 'hello@sanoire.com';
const SOCIAL_HANDLE   = process.env.REACT_APP_SOCIAL_HANDLE   || 'sanoire';
const TG_CHANNEL_NAME = process.env.REACT_APP_TG_CHANNEL_NAME || 'Sanoire Channel';
const CAMPAIGN_BUCKET = process.env.REACT_APP_CAMPAIGN_BUCKET || 'studio-images';
const SMS_RATE_USD    = Number(process.env.REACT_APP_SMS_RATE_USD) || 0.0079; // per segment per recipient

// ── Channel config ─────────────────────────────────────────────────────────────
const CH = {
  email:     { label: 'Email',     color: '#314F5C', pale: '#E0F5FC' },
  push:      { label: 'Push',      color: '#5856d6', pale: '#ede8ff' },
  sms:       { label: 'SMS',       color: '#1a9c3e', pale: '#e6f7ec' },
  telegram:  { label: 'Telegram',  color: '#229ED9', pale: '#e3f4fc' },
  instagram: { label: 'Instagram', color: '#E1306C', pale: '#fce4ec' },
  tiktok:    { label: 'TikTok',    color: '#010101', pale: '#e8e8e8' },
  pinterest: { label: 'Pinterest', color: '#e60023', pale: '#fce8e8' },
};
const CHANNEL_ORDER = ['email','push','sms','telegram','instagram','tiktok','pinterest'];
const BLAST_CHANNELS = ['email','push','sms','telegram'];
const igGrad = 'linear-gradient(135deg,#f09433 0%,#e6683c 22%,#dc2743 48%,#cc2366 74%,#bc1888 100%)';
const _IG_TOKEN = process.env.REACT_APP_IG_TOKEN;
const _IG_ID    = process.env.REACT_APP_IG_ID;

// ── Templates ─────────────────────────────────────────────────────────────────
const BUILTIN_TEMPLATES = {
  email: [
    { label: 'Renewal',          subject: 'Your Sanoire pass expires soon — renew in 60 seconds', body: 'Hi [first_name],\n\nYour Sanoire pass expires on [expiry_date]. Renew in a minute to keep Sana, your saved places and the full atlas at your side wherever you travel.\n\n→ Renew at sanoire.com\n\nTravel with confidence,\nThe Sanoire team' },
    { label: 'Welcome',          subject: 'Welcome to Sanoire — your next trip starts here',      body: 'Hi [first_name],\n\nWelcome aboard. Three ways to start:\n\n1. Ask Sana anything — where to eat, how to get around, what to skip\n2. Read the city guides: editorially curated, honestly scored\n3. Save the places you want to remember\n\nTravel with confidence,\nThe Sanoire team' },
    { label: 'Win-back',         subject: 'It’s been a while — the atlas has grown',          body: 'Hi [first_name],\n\nSince your last trip with us: new cities, deeper guides, and a smarter Sana.\n\nCome back: sanoire.com\n\nThe Sanoire team' },
    { label: 'New in the atlas', subject: 'New places worth knowing in your region',              body: 'Hi [first_name],\n\nWe’ve added new places worth knowing near you — from late-night tables to trusted pharmacies. Open Sanoire to explore.\n\nTravel with confidence,\nThe Sanoire team' },
  ],
  push: [
    { label: 'New in the atlas', title: 'New places near you',    body: 'Fresh additions to the Sanoire atlas in your area. Tap to explore.' },
    { label: 'Renewal',          title: 'Your pass expires soon', body: 'Renew your Sanoire pass in 60 seconds to keep full access while you travel.' },
  ],
  sms: [
    { label: 'Renewal', message: 'Sanoire: Your pass expires in 7 days. Renew at sanoire.com to keep Sana and the full atlas while you travel. Reply STOP to opt out.' },
    { label: 'Welcome', message: 'Welcome to Sanoire! Your pass is active. Ask Sana anything, anywhere you land. sanoire.com' },
  ],
  telegram: [
    { label: 'New country',  message: '🌍 *New: [COUNTRY] coverage launched*\n\nSanoire now covers [COUNTRY] — where to eat, stay, and get things done.\n\n→ [sanoire.com](https://sanoire.com)' },
    { label: 'Feature',      message: '🤖 *Sana update*\n\nOur travel assistant just got smarter. Ask Sana anything about your next city.' },
  ],
  instagram: [
    { label: 'New destination', caption: 'Just added [CITY] to the atlas ✈️ Eat well, move easily, travel with confidence. Link in bio.' },
    { label: 'Travel tip',      caption: 'Before you fly: save these 3 things in your Sanoire app 📍 #travelwithconfidence' },
  ],
  tiktok: [
    { label: 'Travel tip', caption: 'Before you fly: save these 3 things in your Sanoire app 📍 #travel #sanoire' },
  ],
  pinterest: [
    { label: 'Traveller rituals', title: '5 rituals for frequent travellers', description: 'From packing to local pharmacies — Sanoire has you covered.' },
  ],
};

const FIELD_KEYS = {
  email:     ['subject','body','imageUrl','scheduledAt'],
  push:      ['title','body','imageUrl','segment','scheduledAt','deepLink'],
  sms:       ['message','imageUrl','segment','scheduledAt'],
  telegram:  ['message','imageUrl','videoUrl','pinMessage','scheduledAt'],
  instagram: ['caption','imageUrl','videoUrl','schedule'],
  tiktok:    ['videoUrl','caption','hashtags','schedule'],
  pinterest: ['title','description','imageUrl','linkUrl','schedule'],
};
const FIELD_DEFAULTS = {
  email:     { subject:'', body:'', imageUrl:'', segment:'all', scheduledAt:'' },
  push:      { title:'', body:'', imageUrl:'', segment:'all', scheduledAt:'', deepLink:'' },
  sms:       { message:'', imageUrl:'', segment:'all', scheduledAt:'' },
  telegram:  { message:'', imageUrl:'', videoUrl:'', pinMessage:false, scheduledAt:'' },
  instagram: { caption:'', imageUrl:'', videoUrl:'', schedule:'' },
  tiktok:    { videoUrl:'', caption:'', hashtags:'#sanoire #travel', schedule:'' },
  pinterest: { title:'', description:'', imageUrl:'', linkUrl:'', schedule:'' },
};

// ── Utilities ─────────────────────────────────────────────────────────────────
function timeAgo(ts) {
  if (!ts) return '—';
  const ms = Date.now() - new Date(ts);
  if (ms < 3600000)  return Math.round(ms/60000)+'m ago';
  if (ms < 86400000) return Math.round(ms/3600000)+'h ago';
  return Math.round(ms/86400000)+'d ago';
}
function smsCost(msgLen, recipients) {
  return (Math.ceil(msgLen/160||1) * recipients * SMS_RATE_USD).toFixed(2);
}
function pct(n, total) {
  if (!total || !n) return '0%';
  return Math.round(n/total*100)+'%';
}

// ── Hooks ─────────────────────────────────────────────────────────────────────
function useIsMobile() {
  const [mob, setMob] = React.useState(() => typeof window !== 'undefined' && window.innerWidth < 900);
  React.useEffect(() => {
    const h = () => setMob(window.innerWidth < 900);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return mob;
}

function useDraft(channel) {
  const key = 'sanoire_draft_' + channel;
  const [savedAt, setSavedAt] = useState(null);
  const timer = useRef(null);
  const load = useCallback(() => { try { return JSON.parse(localStorage.getItem(key)||'null'); } catch(_e){ return null; } }, [key]);
  const save = useCallback((values) => {
    clearTimeout(timer.current);
    timer.current = setTimeout(() => { localStorage.setItem(key, JSON.stringify(values)); setSavedAt(new Date()); }, 800);
  }, [key]);
  const clear = useCallback(() => { clearTimeout(timer.current); localStorage.removeItem(key); setSavedAt(null); }, [key]);
  return { load, save, clear, savedAt };
}

function useCustomTemplates(channel) {
  const key = 'sanoire_tpl_' + channel;
  const [custom, setCustom] = useState(() => { try { return JSON.parse(localStorage.getItem(key)||'[]'); } catch(_e){ return []; } });
  const saveTpl = useCallback((values, lbl) => {
    const tpl = { label:lbl, ...values, _custom:true, _id:Date.now() };
    setCustom(prev => { const next=[tpl,...prev]; localStorage.setItem(key,JSON.stringify(next)); return next; });
  }, [key]);
  const deleteTpl = useCallback((id) => {
    setCustom(prev => { const next=prev.filter(t=>t._id!==id); localStorage.setItem(key,JSON.stringify(next)); return next; });
  }, [key]);
  return { custom, saveTpl, deleteTpl };
}

// ── Channel icons ─────────────────────────────────────────────────────────────
function ChIcon({ ch, size=16, color='currentColor' }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const s = { width:size, height:size, display:'block', flexShrink:0 };
  if (ch==='email')     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
  if (ch==='instagram') return <svg style={s} viewBox="0 0 24 24" fill={color}><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>;
  if (ch==='tiktok')    return <svg style={s} viewBox="0 0 24 24" fill={color}><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-2.88 2.5 2.89 2.89 0 01-2.89-2.89 2.89 2.89 0 012.89-2.89c.28 0 .54.04.79.1V9.01a6.27 6.27 0 00-.79-.05 6.34 6.34 0 00-6.34 6.34 6.34 6.34 0 006.34 6.34 6.34 6.34 0 006.33-6.34V8.69a8.18 8.18 0 004.78 1.52V6.73a4.85 4.85 0 01-1.01-.04z"/></svg>;
  if (ch==='pinterest') return <svg style={s} viewBox="0 0 24 24" fill={color}><path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738a.36.36 0 01.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/></svg>;
  if (ch==='push')      return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>;
  if (ch==='sms')       return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>;
  if (ch==='telegram')  return <svg style={s} viewBox="0 0 24 24" fill={color}><path d="M11.944 0A12 12 0 000 12a12 12 0 0012 12 12 12 0 0012-12A12 12 0 0012 0a12 12 0 00-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 01.171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>;
  if (ch==='blast')     return <svg style={s} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>;
  return null;
}

// ── Shared styles ─────────────────────────────────────────────────────────────
const inp = (t) => ({ width:'100%', border:`1px solid ${t.bd}`, borderRadius:8, padding:'8px 11px', fontSize:12.5, color:t.tx, background:t.rowbg, fontFamily:F, outline:'none', boxSizing:'border-box' });
const LBL = (t) => ({ fontSize:10, fontWeight:700, color:t.mut, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'.07em', fontFamily:F });

// ── Channel strip card ────────────────────────────────────────────────────────
function ChannelCard({ ch, active, stat, onClick }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const cfg = CH[ch];
  const isIG = ch==='instagram';
  const iconBg = active ? 'rgba(255,255,255,0.15)' : cfg.pale;
  const iconColor = active ? '#fff' : cfg.color;
  return (
    <button onClick={onClick} style={{
      background: active ? (isIG ? igGrad : cfg.color) : '#fff',
      border: 'none', borderRadius:11, padding:'10px 6px 9px',
      cursor:'pointer', textAlign:'center', transition:'all .15s',
      boxShadow: active
        ? `0 4px 14px ${cfg.color}44, 0 0 0 1.5px ${cfg.color}`
        : '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.06)',
      fontFamily:F, flex:1,
    }}>
      <div style={{ width:28, height:28, borderRadius:8, background:iconBg, margin:'0 auto 5px', display:'flex', alignItems:'center', justifyContent:'center' }}>
        <ChIcon ch={ch} size={14} color={iconColor} />
      </div>
      <div style={{ fontSize:9.5, fontWeight:700, color: active ? (isIG ? '#fff' : '#fff') : '#314F5C', letterSpacing:'.02em' }}>{cfg.label}</div>
      <div style={{ fontSize:8.5, color: active ? 'rgba(255,255,255,.65)' : '#9eb8c8', marginTop:1 }}>{stat||'—'}</div>
    </button>
  );
}

// ── Analytics bar ─────────────────────────────────────────────────────────────
function AnalyticsBar({ channel, history, loading }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const sent   = history.reduce((a,c) => a+(c.sent||c.recipients||0), 0);
  const opens  = history.reduce((a,c) => a+(c.open_count||0), 0);
  const clicks = history.reduce((a,c) => a+(c.click_count||0), 0);
  const openRate  = sent>0 ? Math.round(opens/sent*100)+'%' : '—';
  const clickRate = sent>0 ? Math.round(clicks/sent*100)+'%' : '—';
  const count = history.length;

  const tiles = [
    { label:'Total sent',   val: loading ? '…' : sent.toLocaleString(), sub:'all time' },
    { label:'Open rate',    val: loading ? '…' : openRate,  sub:'avg per campaign', accent:'#2563eb' },
    { label:'Click rate',   val: loading ? '…' : clickRate, sub:'per send',         accent:'#9F7E4F' },
    { label:'Campaigns',    val: loading ? '…' : count,     sub:'this channel' },
  ];

  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:10, marginBottom:16 }}>
      {tiles.map(({ label, val, sub, accent }) => (
        <div key={label} style={{ background:t.panel, borderRadius:11, padding:'12px 14px', border:`1px solid ${t.bd}` }}>
          <div style={{ fontSize:22, fontWeight:300, color: accent||'#0d1d2b', lineHeight:1, marginBottom:3, fontFamily:F }}>{val}</div>
          <div style={{ fontSize:9.5, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.06em', fontFamily:F }}>{label}</div>
          <div style={{ fontSize:9, color:'#b0c8d4', marginTop:2, fontFamily:F }}>{sub}</div>
        </div>
      ))}
    </div>
  );
}

// ── Segment selector cards ────────────────────────────────────────────────────
function SegmentCards({ value, onChange, segments, loading }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  return (
    <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:7, marginBottom:14 }}>
      {segments.map(s => {
        const active = value===s.id;
        return (
          <button key={s.id} onClick={() => onChange(s.id)}
            style={{ background: active ? '#E0F5FC' : '#f8fbfd', border: `1.5px solid ${active ? '#314F5C' : 'rgba(111,143,167,0.18)'}`, borderRadius:9, padding:'9px 12px', textAlign:'left', cursor:'pointer', transition:'all .12s', fontFamily:F }}>
            <div style={{ fontSize:11.5, fontWeight:600, color:t.tx }}>{s.label}</div>
            <div style={{ fontSize:10, color:t.mut, marginTop:2 }}>{loading ? '…' : s.count.toLocaleString()} recipients</div>
          </button>
        );
      })}
    </div>
  );
}

// ── Live preview ──────────────────────────────────────────────────────────────
function LivePreview({ channel, values }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const mainKey = ['subject','caption','title','message'].find(k => values[k]!==undefined);
  const mainVal = values[mainKey]||'';

  if (channel==='email') return (
    <div style={{ background:t.rowbg, borderRadius:10, padding:10, fontFamily:F }}>
      <div style={{ fontSize:9, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:7 }}>Inbox preview</div>
      <div style={{ background:t.panel, borderRadius:9, overflow:'hidden', boxShadow:'0 1px 6px rgba(15,30,40,0.08)' }}>
        <div style={{ padding:'8px 11px', borderBottom:'1px solid #f0f4f6', display:'flex', alignItems:'center', gap:7 }}>
          <div style={{ width:24, height:24, borderRadius:'50%', background:'#314F5C', display:'flex', alignItems:'center', justifyContent:'center', fontSize:10, fontWeight:700, color:'#E0F5FC', flexShrink:0 }}>S</div>
          <div style={{ fontSize:11, fontWeight:600, color:t.tx }}>Sanoire <span style={{ fontWeight:400, color:t.mut, fontSize:9.5 }}>&lt;{FROM_EMAIL}&gt;</span></div>
        </div>
        <div style={{ padding:'8px 11px 3px', fontSize:12, fontWeight:600, color: values.subject ? '#0d1d2b' : '#c0ccd4', fontStyle: values.subject ? 'normal' : 'italic', lineHeight:1.3 }}>{values.subject||'Subject line…'}</div>
        <div style={{ padding:'3px 11px 10px', fontSize:11, color:t.mut, lineHeight:1.5, maxHeight:60, overflow:'hidden', whiteSpace:'pre-wrap' }}>{values.body||<span style={{ color:t.mut, fontStyle:'italic' }}>Email body will appear here as you type…</span>}</div>
      </div>
    </div>
  );

  if (channel==='push') return (
    <div style={{ background:t.rowbg, borderRadius:10, padding:10, fontFamily:F }}>
      <div style={{ fontSize:9, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:7 }}>Notification preview</div>
      <div style={{ background:'#1c1c1e', borderRadius:13, padding:'10px 11px', display:'flex', alignItems:'flex-start', gap:9 }}>
        <div style={{ width:30, height:30, borderRadius:8, background:'#5856d6', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff' }}><ChIcon ch='push' size={14} color='#fff' /></div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
            <span style={{ fontSize:11, fontWeight:600, color:'#fff' }}>Sanoire</span>
            <span style={{ fontSize:9.5, color:'rgba(255,255,255,.4)' }}>now</span>
          </div>
          <div style={{ fontSize:11.5, fontWeight:500, color:'#fff', marginBottom:2 }}>{values.title||<span style={{ color:'rgba(255,255,255,.3)', fontStyle:'italic' }}>Title…</span>}</div>
          <div style={{ fontSize:10.5, color:'rgba(255,255,255,.6)', lineHeight:1.4 }}>{values.body||<span style={{ fontStyle:'italic' }}>Body…</span>}</div>
        </div>
      </div>
    </div>
  );

  if (channel==='sms') return (
    <div style={{ background:t.rowbg, borderRadius:10, padding:10, fontFamily:F }}>
      <div style={{ fontSize:9, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:7 }}>SMS preview</div>
      <div style={{ background:t.panel, borderRadius:9, padding:10 }}>
        <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:6 }}>
          <div style={{ background:'#1a9c3e', color:'#fff', borderRadius:'13px 13px 3px 13px', padding:'7px 10px', fontSize:11.5, maxWidth:'85%', lineHeight:1.5, wordBreak:'break-word' }}>
            {values.message||<span style={{ opacity:0.5, fontStyle:'italic' }}>Message…</span>}
          </div>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', fontSize:9, color:t.mut }}>
          <span>{(values.message||'').length}/160</span>
          <span>{Math.ceil((values.message||' ').length/160)} SMS</span>
        </div>
      </div>
    </div>
  );

  if (channel==='telegram') return (
    <div style={{ background:t.rowbg, borderRadius:10, padding:10, fontFamily:F }}>
      <div style={{ fontSize:9, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:7 }}>Telegram preview</div>
      <div style={{ background:'#212d3b', borderRadius:10, padding:12 }}>
        <div style={{ fontSize:11, fontWeight:700, color:'#229ED9', marginBottom:5 }}>{TG_CHANNEL_NAME}</div>
        <div style={{ fontSize:11.5, color:'#e8f4fb', lineHeight:1.6, whiteSpace:'pre-wrap', wordBreak:'break-word' }}>{values.message||<span style={{ opacity:.4, fontStyle:'italic' }}>Message…</span>}</div>
      </div>
    </div>
  );

  return (
    <div style={{ background:t.rowbg, borderRadius:10, padding:10, fontFamily:F }}>
      <div style={{ fontSize:9, fontWeight:700, color:t.mut, textTransform:'uppercase', letterSpacing:'.07em', marginBottom:7 }}>{CH[channel]?.label} preview</div>
      <div style={{ background:t.panel, borderRadius:9, overflow:'hidden' }}>
        <div style={{ padding:'7px 9px', borderBottom:'1px solid #f4f6f8', display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:22, height:22, borderRadius:'50%', background: channel==='instagram' ? igGrad : CH[channel]?.color, flexShrink:0 }} />
          <span style={{ fontSize:11.5, fontWeight:600, color:t.tx }}>{SOCIAL_HANDLE}</span>
        </div>
        <div style={{ height:80, background:'#edf2f5', display:'flex', alignItems:'center', justifyContent:'center' }}>
          {values.imageUrl ? <img src={values.imageUrl} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} /> : <span style={{ fontSize:10, color:t.mut, fontStyle:'italic' }}>Image preview</span>}
        </div>
        <div style={{ padding:'7px 9px', fontSize:11.5, color: mainVal ? '#0d1d2b' : '#c0ccd4', lineHeight:1.5, maxHeight:50, overflow:'hidden', fontStyle: mainVal ? 'normal' : 'italic' }}>{mainVal||'Caption…'}</div>
      </div>
    </div>
  );
}

// ── Campaign history row ──────────────────────────────────────────────────────
function CampaignRow({ item, onCancel, onRetry, onDuplicate }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const STATUS = { sent:{bg:'#eaf6ef',c:'#2d7a4f'}, queued:{bg:'#fff8e6',c:'#9f6c00'}, scheduled:{bg:'#e0f5fc',c:'#185FA5'}, failed:{bg:'#fef0ef',c:'#8b1c1c'}, cancelled:{bg:'#f4f6f8',c:'#9eb8c8'}, sending:{bg:'#f0eeff',c:'#5340b5'} };
  const ss = STATUS[item.status]||STATUS.queued;
  const hasSent = (item.sent||0) > 0;
  const openRate  = hasSent && item.open_count  ? pct(item.open_count,  item.sent) : null;
  const clickRate = hasSent && item.click_count ? pct(item.click_count, item.sent) : null;
  const canCancel = item.status==='scheduled'||item.status==='queued';
  const canRetry  = item.status==='failed'||item.status==='cancelled';

  return (
    <div style={{ padding:'9px 0', borderBottom:'1px solid #f4f6f8' }}>
      <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:6, marginBottom:3 }}>
        <div style={{ fontSize:12, fontWeight:500, color:t.tx, fontFamily:F, flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', lineHeight:1.3 }}>
          {item.subject||item.content?.slice(0,60)||'—'}
        </div>
        <span style={{ padding:'1px 7px', borderRadius:99, fontSize:9, fontWeight:700, color:ss.c, background:ss.bg, fontFamily:F, flexShrink:0 }}>{item.status}</span>
      </div>
      <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
        <span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>{timeAgo(item.created_at)}</span>
        {hasSent && <span style={{ fontSize:9.5, color:'#17a86a', fontFamily:F }}>✓{(item.sent||0).toLocaleString()}</span>}
        {openRate  && <span style={{ fontSize:9.5, color:'#2563eb', fontFamily:F }}>👁 {openRate}</span>}
        {clickRate && <span style={{ fontSize:9.5, color:'#9F7E4F', fontFamily:F }}>↗ {clickRate}</span>}
        {item.failed>0 && <span style={{ fontSize:9.5, color:'#c62828', fontFamily:F }}>✗{item.failed}</span>}
        <div style={{ flex:1 }} />
        {onDuplicate && <button onClick={() => onDuplicate(item)} style={{ fontSize:9.5, color:t.mut, background:'none', border:'none', cursor:'pointer', fontFamily:F, fontWeight:600, padding:0 }}>Dup</button>}
        {canCancel && onCancel && <button onClick={() => onCancel(item)} style={{ fontSize:9.5, color:'#c62828', background:'none', border:'none', cursor:'pointer', fontFamily:F, fontWeight:700, padding:0 }}>Cancel</button>}
        {canRetry  && onRetry  && <button onClick={() => onRetry(item)}  style={{ fontSize:9.5, color:'#314F5C', background:'none', border:'none', cursor:'pointer', fontFamily:F, fontWeight:700, padding:0 }}>Retry</button>}
      </div>
    </div>
  );
}

// ── Right panel ───────────────────────────────────────────────────────────────
function RightPanel({ channel, values, history, histLoading, onCancel, onRetry, onDuplicate }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [view, setView] = useState('preview');
  return (
    <div>
      <div style={{ display:'flex', borderBottom:`1px solid ${t.bd}`, marginBottom:12 }}>
        {['preview','recent'].map(v => (
          <button key={v} onClick={() => setView(v)} style={{ padding:'6px 12px', background:'none', border:'none', borderBottom:`2px solid ${view===v ? '#314F5C' : 'transparent'}`, fontSize:11.5, fontWeight:view===v ? 700 : 400, color:view===v ? '#0f1e28' : '#9eb8c8', cursor:'pointer', fontFamily:F, textTransform:'capitalize' }}>{v}</button>
        ))}
      </div>
      {view==='preview' && <LivePreview channel={channel} values={values} />}
      {view==='recent' && (
        <div>
          {histLoading ? <div style={{ fontSize:11, color:'#b0c8d4', fontFamily:F }}>Loading…</div>
          : history.length===0 ? <div style={{ fontSize:11.5, color:'#b0c8d4', fontStyle:'italic', fontFamily:F, textAlign:'center', padding:'16px 0' }}>No campaigns yet</div>
          : history.map((item, i) => <CampaignRow key={item.id||i} item={item} onCancel={onCancel} onRetry={onRetry} onDuplicate={onDuplicate} />)}
        </div>
      )}
    </div>
  );
}

// ── Template picker ───────────────────────────────────────────────────────────
function TemplatesPicker({ channel, currentValues, onApply }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [showSave, setShowSave] = useState(false);
  const [saveName, setSaveName] = useState('');
  const { custom, saveTpl, deleteTpl } = useCustomTemplates(channel);
  const builtins = BUILTIN_TEMPLATES[channel]||[];
  const all = [...custom, ...builtins];
  return (
    <div style={{ marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
        <span style={LBL(t)}>Templates</span>
        <button onClick={() => setShowSave(s=>!s)} style={{ fontSize:10, color:'#314F5C', background:'none', border:'none', cursor:'pointer', fontFamily:F, fontWeight:700 }}>{showSave ? '× Cancel' : '+ Save current'}</button>
      </div>
      {showSave && (
        <div style={{ display:'flex', gap:6, marginBottom:8 }}>
          <input value={saveName} onChange={e => setSaveName(e.target.value)} placeholder='Template name…' style={{ flex:1, border:`1px solid ${t.bd}`, borderRadius:7, padding:'5px 9px', fontSize:12, fontFamily:F, color:t.tx, outline:'none', background:t.panel }} />
          <button onClick={() => { if (saveName.trim()) { saveTpl(currentValues, saveName.trim()); setSaveName(''); setShowSave(false); }}}
            style={{ padding:'5px 12px', background:'#314F5C', color:'#E0F5FC', border:'none', borderRadius:7, fontSize:11.5, fontWeight:700, cursor:'pointer', fontFamily:F }}>Save</button>
        </div>
      )}
      <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
        {all.map((t, i) => (
          <div key={t._id||i} style={{ display:'flex', alignItems:'center', gap:1 }}>
            <button onClick={() => onApply(t)}
              style={{ padding:'4px 11px', borderRadius:99, border:`1px solid ${t._custom ? '#314F5C' : 'rgba(111,143,167,0.22)'}`, background: t._custom ? 'rgba(49,79,92,0.07)' : '#fff', color:'#314F5C', fontSize:11, cursor:'pointer', fontFamily:F, fontWeight: t._custom ? 700 : 400 }}>
              {t.label}
            </button>
            {t._custom && <button onClick={() => deleteTpl(t._id)} style={{ background:'none', border:'none', cursor:'pointer', color:t.mut, fontSize:13, lineHeight:1, padding:'0 2px' }}>×</button>}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Confirm modal ─────────────────────────────────────────────────────────────
function ConfirmModal({ channel, channels, recipientCount, isTest, preview, onConfirm, onCancel, loading }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isBlast = channels && channels.length>1;
  const color = isBlast ? '#314F5C' : (CH[channel]?.color||'#314F5C');
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(15,30,40,0.55)', zIndex:9200, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }} onClick={e => { if (e.target===e.currentTarget) onCancel(); }}>
      <div style={{ background:t.panel, borderRadius:16, width:'100%', maxWidth:400, boxShadow:'0 32px 80px rgba(0,0,0,0.25)', overflow:'hidden', fontFamily:F }}>
        <div style={{ padding:'16px 20px 14px', borderBottom:`1px solid ${t.bd}`, display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:38, height:38, borderRadius:11, background: channel==='instagram' && !isBlast ? igGrad : color, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#fff' }}>
            {isBlast ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg> : <ChIcon ch={channel} size={18} color='#fff' />}
          </div>
          <div>
            <div style={{ fontSize:13.5, fontWeight:700, color:'#0f1e28' }}>{isTest ? 'Send test to yourself' : isBlast ? `Blast to ${channels.length} channels` : `Send ${CH[channel]?.label||channel} campaign`}</div>
            <div style={{ fontSize:11, color:t.mut, marginTop:1 }}>{isTest ? 'Test send — goes only to you' : `${recipientCount.toLocaleString()} recipients`}</div>
          </div>
        </div>
        <div style={{ padding:'14px 20px 18px' }}>
          <div style={{ background:'#f8fafc', border:`1px solid ${t.bd}`, borderRadius:9, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#314F5C', lineHeight:1.5, maxHeight:80, overflow:'hidden' }}>{preview||'(empty)'}</div>
          <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
            <button onClick={onCancel} disabled={loading} style={{ padding:'8px 16px', background:'transparent', border:`1px solid ${t.bd}`, borderRadius:8, fontSize:12, color:'#5a7a8a', cursor:'pointer', fontFamily:F }}>Cancel</button>
            <button onClick={onConfirm} disabled={loading}
              style={{ padding:'8px 22px', background: loading ? '#e4edf3' : (channel==='instagram' && !isBlast ? igGrad : color), color: loading ? '#9eb8c8' : '#fff', border:'none', borderRadius:8, fontSize:12, fontWeight:700, cursor: loading ? 'default' : 'pointer', fontFamily:F }}>
              {loading ? 'Sending…' : isTest ? 'Send test' : 'Send now'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Action row ────────────────────────────────────────────────────────────────
function ActionRow({ channel, canSend, recipientCount, onTest, onSend, sending, scheduledAt }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const color = CH[channel]?.color||'#314F5C';
  return (
    <div style={{ display:'flex', gap:8, justifyContent:'flex-end', alignItems:'center', marginTop:14 }}>
      <button onClick={onTest} disabled={!canSend}
        style={{ display:'flex', alignItems:'center', gap:5, padding:'8px 14px', background:'transparent', border:`1.5px solid ${canSend ? color+'44' : '#dde8ed'}`, borderRadius:8, fontSize:11.5, color: canSend ? color : '#c0ccd4', cursor: canSend ? 'pointer' : 'default', fontFamily:F, fontWeight:600 }}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M4 4l16 8-16 8V4z"/></svg>
        Test
      </button>
      <button onClick={onSend} disabled={!canSend||sending}
        style={{ display:'flex', alignItems:'center', gap:6, padding:'9px 18px', background: !canSend||sending ? '#e4edf3' : (channel==='instagram' ? igGrad : color), color: !canSend||sending ? '#9eb8c8' : '#fff', border:'none', borderRadius:9, fontSize:12, fontWeight:700, cursor: canSend&&!sending ? 'pointer' : 'default', fontFamily:F, boxShadow: canSend&&!sending ? `0 3px 10px ${color}44` : 'none' }}>
        {sending ? 'Sending…' : scheduledAt ? 'Schedule →' : channel==='telegram' ? 'Broadcast →' : 'Send →'}
        {!sending && recipientCount>0 && <span style={{ fontSize:10, opacity:.7, fontWeight:400 }}>{recipientCount.toLocaleString()}</span>}
      </button>
    </div>
  );
}

// ── Upload image ──────────────────────────────────────────────────────────────
async function uploadImage(file) {
  const ext = (file.name.split('.').pop()||'jpg').toLowerCase();
  const path = 'campaign-media/img-'+Date.now()+'.'+ext;
  const { error } = await supabase.storage.from(CAMPAIGN_BUCKET).upload(path, file, { contentType:file.type });
  if (error) throw new Error(error.message);
  const { data:{ publicUrl } } = supabase.storage.from(CAMPAIGN_BUCKET).getPublicUrl(path);
  return publicUrl;
}
async function uploadVideo(file) {
  const ext = (file.name.split('.').pop()||'mp4').toLowerCase();
  const path = 'campaign-media/vid-'+Date.now()+'.'+ext;
  const { error } = await supabase.storage.from(CAMPAIGN_BUCKET).upload(path, file, { contentType:file.type });
  if (error) throw new Error(error.message);
  const { data:{ publicUrl } } = supabase.storage.from(CAMPAIGN_BUCKET).getPublicUrl(path);
  return publicUrl;
}

// ── MediaDropZone — shared drag-and-drop upload component ──────────────────
function MediaDropZone({ accept, onFile, uploading, url, previewIsVideo, onClear, hint }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [dragOver, setDragOver] = React.useState(false);
  const inputRef = React.useRef(null);
  const hasMedia = !!url;
  function onDrop(e) {
    e.preventDefault(); e.stopPropagation();
    setDragOver(false);
    if (uploading) return;
    const file = (e.dataTransfer.files || [])[0];
    if (file && onFile) onFile(file);
  }
  function onDragOver(e) { e.preventDefault(); setDragOver(true); }
  function onDragLeave(e) { e.preventDefault(); setDragOver(false); }
  return (
    <div onDrop={onDrop} onDragOver={onDragOver} onDragLeave={onDragLeave}
      style={{ position:'relative', borderRadius:10, transition:'all .15s',
        border: '1.5px dashed ' + (dragOver ? '#314F5C' : hasMedia ? 'rgba(49,79,92,0.15)' : '#c8d8e4'),
        background: dragOver ? 'rgba(49,79,92,0.04)' : hasMedia ? 'transparent' : '#fafcfd',
        overflow: 'hidden', cursor: uploading ? 'wait' : hasMedia ? 'default' : 'pointer' }}
      onClick={() => !hasMedia && !uploading && inputRef.current?.click()}>
      {hasMedia ? (
        <div style={{ position:'relative', width:'100%' }}>
          {previewIsVideo
            ? <video src={url} controls style={{ width:'100%', maxHeight:140, display:'block', objectFit:'contain', background:'#0f1e28' }} />
            : <img src={url} alt='' style={{ width:'100%', height:80, objectFit:'cover', display:'block' }} />}
          {/* drag-to-replace overlay */}
          {dragOver && (
            <div style={{ position:'absolute', inset:0, background:'rgba(13,29,43,0.72)', display:'flex', alignItems:'center', justifyContent:'center' }}>
              <span style={{ color:'#E0F5FC', fontSize:12, fontWeight:600, fontFamily:F }}>Drop to replace</span>
            </div>
          )}
          {/* controls overlay */}
          <div style={{ position:'absolute', top:6, right:6, display:'flex', gap:4 }}>
            <button onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}
              style={{ padding:'3px 8px', background:'rgba(13,29,43,0.68)', border:'none', borderRadius:5,
                color:'#E0F5FC', fontSize:10, fontWeight:600, cursor:'pointer', fontFamily:F }}>
              Replace
            </button>
            <button onClick={e => { e.stopPropagation(); onClear(); }}
              style={{ padding:'2px 7px', background:'rgba(13,29,43,0.68)', border:'none', borderRadius:5,
                color:'#E0F5FC', fontSize:13, cursor:'pointer', fontFamily:F, lineHeight:1 }}>
              \u00d7
            </button>
          </div>
        </div>
      ) : (
        <div style={{ textAlign:'center', padding:'16px 14px', userSelect:'none' }}>
          {uploading ? (
            <div style={{ fontSize:12, color:t.mut, fontFamily:F }}>Uploading\u2026</div>
          ) : (
            <>
              <svg width='22' height='22' viewBox='0 0 24 24' fill='none' stroke={dragOver ? '#314F5C' : '#9eb8c8'}
                strokeWidth='1.6' strokeLinecap='round' style={{ marginBottom:7, display:'block', margin:'0 auto 7px' }}>
                <path d='M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4'/>
                <polyline points='17 8 12 3 7 8'/><line x1='12' y1='3' x2='12' y2='15'/>
              </svg>
              <div style={{ fontSize:11.5, color: dragOver ? '#314F5C' : '#6F8FA7', fontFamily:F, fontWeight:500, marginBottom:3 }}>
                {dragOver ? 'Drop to upload' : ('Drag & drop ' + (hint || 'a file') + ' here')}
              </div>
              <div style={{ fontSize:10.5, color:'#b0c8d4', fontFamily:F }}>
                or <span style={{ color:'#314F5C', fontWeight:600, cursor:'pointer', textDecoration:'underline' }}
                  onClick={e => { e.stopPropagation(); inputRef.current?.click(); }}>browse</span>
              </div>
            </>
          )}
        </div>
      )}
      <input ref={inputRef} type='file' accept={accept} style={{ display:'none' }}
        onChange={e => { if (e.target.files[0]) { onFile(e.target.files[0]); e.target.value=''; }}} />
    </div>
  );
}

// ── Email tab ─────────────────────────────────────────────────────────────────
function EmailTab({ segments, segLoading, isMobile, userEmail, onHistoryChange }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const { load, save, clear, savedAt } = useDraft('email');
  const init = load()||{ subject:'', body:'', imageUrl:'', segment:'all', scheduledAt:'' };
  const [subject,     setSubject]     = useState(init.subject);
  const [body,        setBody]        = useState(init.body);
  const [imageUrl,    setImageUrl]    = useState(init.imageUrl||'');
  const [segment,     setSegment]     = useState(init.segment);
  const [scheduledAt, setScheduledAt] = useState(init.scheduledAt);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [history,     setHistory]     = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [sending,     setSending]     = useState(false);
  const [result,      setResult]      = useState(null);
  const [confirm,     setConfirm]     = useState(null);

  const values = { subject, body, imageUrl, segment, scheduledAt };
  useEffect(() => { save(values); }, [subject, body, imageUrl, segment, scheduledAt]); // eslint-disable-line

  const loadHistory = useCallback(async () => {
    const { data } = await supabase.from('email_campaigns').select('id,subject,body,image_url,scheduled_at,segment,recipients,sent,failed,open_count,click_count,status,created_at').order('created_at',{ascending:false}).limit(20);
    const h = data||[];
    setHistory(h); setHistLoading(false);
    if (onHistoryChange) onHistoryChange(h);
  }, [onHistoryChange]);
  useEffect(() => { loadHistory().catch(_e => setHistLoading(false)); }, [loadHistory]);

  async function handleEmailUpload(file) {
    setUploadingImg(true);
    try { setImageUrl(await uploadImage(file)); } catch (_e) { setResult({ ok:false, msg:'Upload failed: '+_e.message }); }
    setUploadingImg(false);
  }
  const selectedSeg = segments.find(s => s.id===segment);
  const recipientCount = selectedSeg?.count||0;
  const subjectLen = subject.length;
  const canSend = subject.trim().length>0 && body.trim().length>0;

  async function doSend(isTest) {
    setSending(true); setResult(null);
    try {
      if (scheduledAt && !isTest) {
        const row = { subject:subject.trim(), body:body.trim(), image_url:imageUrl||null, scheduled_at:new Date(scheduledAt).toISOString(), segment, recipients:recipientCount, status:'scheduled' };
        const { data, error } = await supabase.from('email_campaigns').insert(row).select().single();
        if (error) throw new Error(error.message);
        setResult({ ok:true, msg:'Campaign scheduled.' });
        setHistory(prev => [data,...prev].slice(0,20));
        setSubject(''); setBody(''); setImageUrl(''); setScheduledAt(''); clear();
      } else {
        const row = { subject:subject.trim(), body:body.trim(), image_url:imageUrl||null, segment: isTest?'test':segment, recipients: isTest?1:recipientCount, status:'sending' };
        const { data:campaign, error } = await supabase.from('email_campaigns').insert(row).select().single();
        if (error) throw new Error(error.message);
        const resp = await fetch('/api/send-email', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ subject:subject.trim(), body:body.trim(), imageUrl:imageUrl||undefined, segment: isTest?'test':segment, ...(isTest?{recipients:[userEmail]}:{}) }) });
        const apiResult = await resp.json();
        const finalSent = apiResult.sent||0, finalFailed = apiResult.failed||0;
        const finalStatus = apiResult.setup ? 'queued' : finalSent>0 ? 'sent' : finalFailed>0 ? 'failed' : 'queued';
        await supabase.from('email_campaigns').update({ sent:finalSent, failed:finalFailed, status:finalStatus }).eq('id', campaign.id);
        const updated = { ...campaign, sent:finalSent, failed:finalFailed, status:finalStatus };
        setHistory(prev => [updated,...prev].slice(0,20));
        if (apiResult.setup) {
          setResult({ ok:false, msg:'Resend not configured — add RESEND_API_KEY to Vercel.' });
        } else if (apiResult.error && finalSent===0) {
          setResult({ ok:false, msg:apiResult.error });
        } else {
          setResult({ ok:true, msg: isTest ? `Test sent to ${userEmail}` : `Sent to ${finalSent} recipient${finalSent!==1?'s':''}${finalFailed>0?`, ${finalFailed} failed`:''}` });
          if (!isTest) { setSubject(''); setBody(''); setImageUrl(''); setScheduledAt(''); clear(); }
        }
      }
    } catch (_e) { setResult({ ok:false, msg:_e.message||'Failed.' }); }
    setSending(false); setConfirm(null);
  }

  async function handleCancel(item) { await supabase.from('email_campaigns').update({status:'cancelled'}).eq('id',item.id); setHistory(prev=>prev.map(h=>h.id===item.id?{...h,status:'cancelled'}:h)); }
  async function handleRetry(item)  { await supabase.from('email_campaigns').update({status:'queued'}).eq('id',item.id);    setHistory(prev=>prev.map(h=>h.id===item.id?{...h,status:'queued'}:h)); }
  function handleDuplicate(item) { setSubject(item.subject||''); setBody(item.body||''); setSegment(item.segment||'all'); setResult(null); }

  return (
    <div>
      {confirm && <ConfirmModal channel='email' recipientCount={recipientCount} isTest={confirm==='test'} preview={subject} onConfirm={() => doSend(confirm==='test')} onCancel={() => setConfirm(null)} loading={sending} />}
      <AnalyticsBar channel='email' history={history} loading={histLoading} />
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 270px', gap:14, alignItems:'start' }}>
        <div style={{ background:t.panel, borderRadius:14, padding:'18px 20px', border:`1px solid ${t.bd}` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <span style={{ fontSize:14, fontWeight:700, color:t.tx, fontFamily:F }}>Compose email</span>
            {savedAt && <span style={{ fontSize:10, color:t.mut, fontFamily:F, fontStyle:'italic' }}>Draft saved</span>}
          </div>
          <TemplatesPicker channel='email' currentValues={values} onApply={t => { if (t.subject) setSubject(t.subject); if (t.body) setBody(t.body); }} />
          <div style={{ marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:5 }}>
              <label style={LBL(t)}>Subject</label>
              <span style={{ fontSize:9.5, color: subjectLen>60 ? '#e53935' : '#9eb8c8', fontFamily:F }}>{subjectLen}/60</span>
            </div>
            <input style={inp(t)} value={subject} onChange={e => setSubject(e.target.value)} placeholder='Email subject line…' maxLength={100} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={LBL(t)}>Audience</label>
            <SegmentCards value={segment} onChange={setSegment} segments={segments} loading={segLoading} />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={LBL(t)}>Body</label>
            <textarea style={{ ...inp(t), minHeight:120, resize:'vertical', lineHeight:1.65 }} value={body} onChange={e => setBody(e.target.value)} placeholder='Write your message…' />
          </div>
          <div style={{ marginBottom:12 }}>
            <label style={LBL(t)}>Image <span style={{ color:t.mut, fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10 }}>(optional)</span></label>
            <MediaDropZone accept='image/*' onFile={handleEmailUpload} uploading={uploadingImg}
              url={imageUrl} previewIsVideo={false} onClear={() => setImageUrl('')} hint='an image' />
            {!imageUrl && (
              <input style={{ ...inp(t), marginTop:5, fontSize:11, color:t.mut }} value={imageUrl}
                onChange={e => setImageUrl(e.target.value)} placeholder='Or paste an image URL…' />
            )}
          </div>
          <div style={{ marginBottom:12 }}>
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:5 }}>
              <label style={{ ...LBL(t), marginBottom:0 }}>Schedule <span style={{ color:t.mut, fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10 }}>(optional)</span></label>
              <span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>{USER_TZ}</span>
            </div>
            <input type='datetime-local' style={inp(t)} value={scheduledAt} onChange={e => setScheduledAt(e.target.value)} />
          </div>
          {result && <div style={{ padding:'8px 12px', borderRadius:8, background: result.ok ? '#eaf6ef' : '#fef0ef', color: result.ok ? '#2d7a4f' : '#8b1c1c', fontSize:12, fontFamily:F, marginBottom:4 }}>{result.msg}</div>}
          <ActionRow channel='email' canSend={canSend} recipientCount={recipientCount} onTest={() => setConfirm('test')} onSend={() => setConfirm('send')} sending={sending} scheduledAt={scheduledAt} />
        </div>
        <div style={{ background:t.panel, borderRadius:14, padding:'14px 16px', border:`1px solid ${t.bd}` }}>
          <RightPanel channel='email' values={values} history={history} histLoading={histLoading} onCancel={handleCancel} onRetry={handleRetry} onDuplicate={handleDuplicate} />
        </div>
      </div>
    </div>
  );
}

// ── Generic channel tab ───────────────────────────────────────────────────────
function ChannelTab({ channel, segments, isMobile, userEmail }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const { load, save, clear, savedAt } = useDraft(channel);
  const init = load()||FIELD_DEFAULTS[channel]||{};
  const [values, setValues] = useState(init);
  const set = (k, v) => setValues(p => ({ ...p, [k]:v }));
  const [history, setHistory] = useState([]);
  const [histLoading, setHistLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [uploading,      setUploading]      = useState(false);
  const [uploadingVideo, setUploadingVideo] = useState(false);

  useEffect(() => { save(values); }, [values]); // eslint-disable-line
  useEffect(() => {
    const tbl = (channel==='push'||channel==='sms'||channel==='telegram') ? null : 'social_posts';
    const emailCh = (channel==='push'||channel==='sms'||channel==='telegram');
    if (emailCh) {
      supabase.from('social_posts').select('id,platform,content,scheduled_at,status,created_by,created_at').eq('platform',channel).order('created_at',{ascending:false}).limit(20)
        .then(({ data }) => { setHistory((data||[]).map(r=>({...r,subject:r.content,recipients:0}))); setHistLoading(false); })
        .catch(_e => setHistLoading(false));
    } else {
      if (!tbl) { setHistLoading(false); return; }
      supabase.from(tbl).select('id,platform,content,scheduled_at,status,created_by,created_at').eq('platform',channel).order('created_at',{ascending:false}).limit(20)
        .then(({ data }) => { setHistory((data||[]).map(r=>({...r,subject:r.content,recipients:0}))); setHistLoading(false); })
        .catch(_e => setHistLoading(false));
    }
  }, [channel]);

  const mainKey = ['caption','title','message'].find(k => FIELD_KEYS[channel]?.includes(k))||'caption';
  const mainVal = values[mainKey]||'';
  const recipSeg = segments?.find(s => s.id===values.segment);
  const recipCount = recipSeg?.count||0;
  const canSend = mainVal.trim().length>0;

  async function doSend(isTest) {
    setSending(true);
    try {
      const content = mainVal.trim();
      const schedAt = values.schedule||values.scheduledAt||null;
      // For push/telegram/sms: call the API first, then log to social_posts
      // This avoids RLS errors blocking the send
      // Robust fetch — surface the HTTP status + raw body when the response isn't JSON,
      // instead of crashing with "Unexpected token 'A'..." on Vercel's HTML error page.
      const safeFetch = async (url, opts) => {
        const r = await fetch(url, opts);
        const text = await r.text();
        try { return { ok: r.ok, status: r.status, data: JSON.parse(text) }; }
        catch (_e) {
          const snippet = (text || '').slice(0, 140).replace(/\s+/g, ' ');
          return { ok: false, status: r.status, data: { error: `HTTP ${r.status} — non-JSON response: ${snippet || '(empty body)'}` } };
        }
      };

      const isPushLike = channel==='push'||channel==='telegram'||channel==='sms';
      let apiResult = null;
      if (isPushLike && !isTest && !schedAt) {
        if (channel==='telegram') {
          const r = await safeFetch('/api/send-telegram', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ message:content, pin:!!values.pinMessage }) });
          apiResult = r.data;
        } else if (channel==='push') {
          const r = await safeFetch('/api/send-push', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ title:values.title||'', message:values.body||content, segment:values.segment||'all', data: values.deepLink?{url:values.deepLink}:{} }) });
          apiResult = r.data;
        }
        if (apiResult?.setup) {
          const envVar = channel==='telegram' ? 'TELEGRAM_BOT_TOKEN + TELEGRAM_CHANNEL_ID' : 'FIREBASE_SERVICE_ACCOUNT';
          setResult({ ok:false, msg:`Not configured — add ${envVar} to Vercel env vars.` });
          setSending(false); setConfirm(null); return;
        }
        const st = apiResult?.sent>0 ? 'sent' : apiResult?.error ? 'failed' : 'queued';
        // Log to social_posts (best-effort — RLS may block, that's ok).
        // Supabase v2 query builder isn't a native Promise; .catch() throws —
        // use try/await/catch instead.
        try {
          await supabase.from('social_posts').insert({ platform:channel, content, status:st, created_by:(userEmail||'admin').split('@')[0] });
        } catch (_e) { /* swallow */ }
        if (apiResult?.sent>0) {
          setResult({ ok:true, msg:`Sent to ${apiResult.sent} recipient${apiResult.sent!==1?'s':''}${apiResult.failed>0?`, ${apiResult.failed} failed`:''}` });
          setValues(FIELD_DEFAULTS[channel]||{}); clear();
        } else {
          setResult({ ok:false, msg: apiResult?.error || 'No recipients found for this segment.' });
        }
        setSending(false); setConfirm(null); return;
      }
      if (isPushLike && isTest && channel==='push') {
        // Test send for push — send directly to all tokens (segment=all returns registered devices)
        const r = await safeFetch('/api/send-push', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ title:'[TEST] '+(values.title||''), message:values.body||content, segment:'all' }) });
        apiResult = r.data;
        setResult(apiResult?.sent>0
          ? { ok:true, msg:`Test sent to ${apiResult.sent} device${apiResult.sent!==1?'s':''}` }
          : { ok:false, msg: apiResult?.note || apiResult?.error || 'No push tokens found.' });
        setSending(false); setConfirm(null); return;
      }
      // Social / other channels — original flow
      const row = { platform:channel, content: isTest?'[TEST] '+content:content, status: schedAt&&!isTest?'scheduled':'queued', created_by:(userEmail||'admin').split('@')[0] };
      if (schedAt&&!isTest) row.scheduled_at = schedAt;
      const { data:dbRow, error } = await supabase.from('social_posts').insert(row).select().single();
      if (error) throw new Error(error.message);
      setHistory(prev=>[{...dbRow,subject:dbRow.content,recipients:0},...prev].slice(0,20));
      setResult({ ok:true, msg: isTest ? 'Test queued' : schedAt ? 'Scheduled.' : 'Queued.' });
      if (!isTest) { setValues(FIELD_DEFAULTS[channel]||{}); clear(); }
    } catch (_e) { setResult({ ok:false, msg:_e.message||'Failed.' }); }
    setSending(false); setConfirm(null);
  }

  async function handleUpload(file) {
    setUploading(true);
    try { set('imageUrl', await uploadImage(file)); } catch (_e) { setResult({ ok:false, msg:'Upload failed: '+_e.message }); }
    setUploading(false);
  }
  async function handleVideoUpload(file) {
    setUploadingVideo(true);
    try { set('videoUrl', await uploadVideo(file)); } catch (_e) { setResult({ ok:false, msg:'Upload failed: '+_e.message }); }
    setUploadingVideo(false);
  }

  const TITLES = { push:'Push Notification', sms:'Compose SMS', telegram:'Broadcast Message', tiktok:'TikTok Post', pinterest:'Create Pin', instagram:'Instagram Post' };
  const LABELS = { caption:'Caption', title:'Title', message:'Message', description:'Description', videoUrl:'Video', hashtags:'Hashtags', imageUrl:'Image', linkUrl:'Link URL', body:'Body', deepLink:'Deep link', schedule:'Schedule', scheduledAt:'Schedule', pinMessage:'Pin in channel', segment:'Audience' };
  const MAXLEN = { caption:2200, title:100, message:160, body:200 };

  return (
    <div>
      {confirm && <ConfirmModal channel={channel} recipientCount={recipCount} isTest={confirm==='test'} preview={mainVal} onConfirm={() => doSend(confirm==='test')} onCancel={() => setConfirm(null)} loading={sending} />}
      <AnalyticsBar channel={channel} history={history} loading={histLoading} />
      <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 270px', gap:14, alignItems:'start' }}>
        <div style={{ background:t.panel, borderRadius:14, padding:'18px 20px', border:`1px solid ${t.bd}` }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
            <span style={{ fontSize:14, fontWeight:700, color:t.tx, fontFamily:F }}>{TITLES[channel]||channel}</span>
            {savedAt && <span style={{ fontSize:10, color:t.mut, fontFamily:F, fontStyle:'italic' }}>Draft saved</span>}
          </div>
          <TemplatesPicker channel={channel} currentValues={values} onApply={t => setValues(p => ({...p,...Object.fromEntries(Object.entries(t).filter(([k])=>!k.startsWith('_')&&k!=='label'))}))} />

          {FIELD_KEYS[channel]?.map(key => {
            const lbl = LABELS[key]||key;
            const isSchedule = key==='schedule'||key==='scheduledAt';
            if (isSchedule) return (
              <div key={key} style={{ marginBottom:12 }}>
                <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:5 }}>
                  <label style={{ ...LBL(t), marginBottom:0 }}>{lbl} <span style={{ color:t.mut, fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10 }}>(optional)</span></label>
                  <span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>{USER_TZ}</span>
                </div>
                <input type='datetime-local' style={inp(t)} value={values[key]||''} onChange={e => set(key, e.target.value)} />
              </div>
            );
            if (key==='segment') return (
              <div key={key} style={{ marginBottom:14 }}>
                <label style={LBL(t)}>{lbl}</label>
                <SegmentCards value={values[key]||'all'} onChange={v => set(key, v)} segments={segments||[]} loading={false} />
              </div>
            );
            if (key==='imageUrl') return (
              <div key={key} style={{ marginBottom:12 }}>
                <label style={LBL(t)}>{lbl}</label>
                <MediaDropZone accept='image/*' onFile={f => handleUpload(f)} uploading={uploading}
                  url={values[key]||''} previewIsVideo={false} onClear={() => set(key, '')} hint='an image' />
                {!values[key] && (
                  <input style={{ ...inp(t), marginTop:5, fontSize:11, color:t.mut }} value={values[key]||''}
                    onChange={e => set(key, e.target.value)} placeholder='Or paste an image URL…' />
                )}
              </div>
            );
            if (key==='videoUrl') return (
              <div key={key} style={{ marginBottom:12 }}>
                <label style={LBL(t)}>{lbl}</label>
                <MediaDropZone accept='video/*' onFile={f => handleVideoUpload(f)} uploading={uploadingVideo}
                  url={values[key]||''} previewIsVideo={true} onClear={() => set(key, '')} hint='a video' />
                {!values[key] && (
                  <input style={{ ...inp(t), marginTop:5, fontSize:11, color:t.mut }} value={values[key]||''}
                    onChange={e => set(key, e.target.value)} placeholder='Or paste a video URL…' />
                )}
              </div>
            );
            if (key==='pinMessage') return (
              <div key={key} style={{ marginBottom:12 }}>
                <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:'#314F5C', fontFamily:F }}>
                  <input type='checkbox' checked={!!values[key]} onChange={e => set(key, e.target.checked)} style={{ accentColor:'#229ED9', width:14, height:14 }} />
                  Pin message in channel
                </label>
              </div>
            );
            const isTextarea = key===mainKey||key==='description'||key==='body';
            const maxLen = MAXLEN[key]||null;
            const curLen = (values[key]||'').length;
            return (
              <div key={key} style={{ marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'baseline', marginBottom:5 }}>
                  <label style={{ ...LBL(t), marginBottom:0 }}>{lbl}</label>
                  {maxLen && <span style={{ fontSize:9.5, color: curLen>maxLen*0.9 ? '#e53935' : '#9eb8c8', fontFamily:F }}>{curLen}/{maxLen}</span>}
                </div>
                {isTextarea
                  ? <textarea style={{ ...inp(t), minHeight: key===mainKey?100:70, resize:'vertical', lineHeight:1.6, fontFamily: channel==='telegram'&&key==='message' ? 'monospace' : F }} value={values[key]||''} onChange={e => set(key, e.target.value)} placeholder={key===mainKey?`Write your ${CH[channel]?.label} ${key}…`:''} maxLength={maxLen||undefined} />
                  : <input style={inp(t)} value={values[key]||''} onChange={e => set(key, e.target.value)} placeholder={key==='linkUrl'?'https://sanoire.com/…':key==='hashtags'?'#sanoire #travel':key==='deepLink'?'sanoire://map?country=TH':''} maxLength={maxLen||undefined} />}
              </div>
            );
          })}

          {channel==='sms' && (
            <div style={{ marginBottom:12 }}>
              <div style={{ padding:'9px 12px', borderRadius:8, background:'#fff8e6', border:'1px solid #f5d87f', fontSize:11.5, color:'#7c4a0a', fontFamily:F, lineHeight:1.5 }}>
                ⚠️ Always include a STOP opt-out for TCPA / GDPR compliance.
              </div>
              {(values.message||'').length>0 && recipCount>0 && (
                <div style={{ marginTop:6, padding:'7px 12px', borderRadius:8, background:t.rowbg, fontSize:11, color:'#5a7a8a', fontFamily:F }}>
                  Est. cost: <strong>${smsCost((values.message||'').length, recipCount)}</strong>
                </div>
              )}
            </div>
          )}

          {result && <div style={{ padding:'8px 12px', borderRadius:8, background: result.ok?'#eaf6ef':'#fef0ef', color: result.ok?'#2d7a4f':'#8b1c1c', fontSize:12, fontFamily:F, marginBottom:4 }}>{result.msg}</div>}
          <ActionRow channel={channel} canSend={canSend} recipientCount={recipCount} onTest={() => setConfirm('test')} onSend={() => setConfirm('send')} sending={sending} scheduledAt={values.schedule||values.scheduledAt} />
        </div>

        <div style={{ background:t.panel, borderRadius:14, padding:'14px 16px', border:`1px solid ${t.bd}` }}>
          <RightPanel channel={channel} values={values} history={history} histLoading={histLoading}
            onCancel={async item => { await supabase.from('social_posts').update({status:'cancelled'}).eq('id',item.id); setHistory(prev=>prev.map(h=>h.id===item.id?{...h,status:'cancelled'}:h)); }}
            onRetry={async item =>  { await supabase.from('social_posts').update({status:'queued'}).eq('id',item.id);    setHistory(prev=>prev.map(h=>h.id===item.id?{...h,status:'queued'}:h)); }}
            onDuplicate={item => setValues(p => ({ ...p, [mainKey]:item.content||'' }))}
          />
        </div>
      </div>
      {isMobile && <div style={{ marginTop:14 }}><LivePreview channel={channel} values={values} /></div>}
      {channel==='push' && <InAppBanners />}
    </div>
  );
}

// ── In-App Banners ───────────────────────────────────────────────────────────
const BANNER_TYPES = [
  { id:'info',    label:'Info',    color:'#314F5C', bg:'rgba(49,79,92,0.08)',   border:'rgba(49,79,92,0.18)' },
  { id:'success', label:'Success', color:'#1b5e20', bg:'rgba(27,94,32,0.10)',   border:'rgba(27,94,32,0.25)' },
  { id:'warn',    label:'Warning', color:'#d97706', bg:'rgba(217,119,6,0.10)',  border:'rgba(217,119,6,0.25)' },
  { id:'urgent',  label:'Urgent',  color:'#c0392b', bg:'rgba(192,57,43,0.10)',  border:'rgba(192,57,43,0.25)' },
];
const SUPA_BANNERS = 'https://dekxnqbjzgkvltclthqw.supabase.co';

async function bannerRest(method='GET', body=null) {
  const r = await fetch('/api/app-notices', {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body ? JSON.stringify(body) : undefined,
  });
  return r.ok ? r.json().catch(()=>[]) : [];
}

function InAppBanners() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving,  setSaving]  = useState(false);
  const [result,  setResult]  = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [alsoPush, setAlsoPush] = useState(false);
  const [draft, setDraft] = useState({ title:'', body:'', type:'info', starts_at:'', ends_at:'' });
  const setD = (k,v) => setDraft(p=>({...p,[k]:v}));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await bannerRest('GET');
      setBanners(Array.isArray(rows) ? rows : []);
    } catch(_e) {}
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  async function toggle(id, active) {
    await bannerRest('PATCH', { id, active });
    setBanners(prev => prev.map(b => b.id===id ? {...b,active} : b));
  }
  async function remove(id) {
    await bannerRest('DELETE', { id });
    setBanners(prev => prev.filter(b => b.id!==id));
  }
  async function create() {
    if (!draft.title.trim()||!draft.body.trim()) return;
    setSaving(true); setResult(null);
    try {
      const row = { title:draft.title.trim(), body:draft.body.trim(), type:draft.type, active:false,
        starts_at:draft.starts_at||null, ends_at:draft.ends_at||null };
      const res = await bannerRest('POST', row);
      if (Array.isArray(res)&&res[0]) {
        setBanners(prev=>[res[0],...prev]);
        let msg = 'Banner created (inactive). Activate it when ready.';
        if (alsoPush) {
          try {
            const pr = await fetch('/api/send-push', { method:'POST', headers:{'Content-Type':'application/json'},
              body: JSON.stringify({ title:draft.title.trim(), message:draft.body.trim(), segment:'all' }) });
            const pd = await pr.json();
            msg = 'Banner live' + (pd.sent>0 ? ' + push sent to '+pd.sent+' device'+(pd.sent!==1?'s':'') : '') + '.';
          } catch(_e) {}
        }
        setResult({ ok:true, msg });
        setDraft({ title:'', body:'', type:'info', starts_at:'', ends_at:'' });
        setShowNew(false); setAlsoPush(false);
      } else { setResult({ ok:false, msg:'Failed — check Supabase permissions.' }); }
    } catch(e) { setResult({ ok:false, msg:e.message }); }
    setSaving(false);
  }

  const tfmt = ts => ts ? new Date(ts).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : null;
  const canCreate = draft.title.trim()&&draft.body.trim()&&!saving;

  return (
    <div style={{ marginTop:24, fontFamily:F }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <div>
          <span style={{ fontSize:14, fontWeight:700, color:t.tx, fontFamily:F }}>In-App Banners</span>
          <div style={{ fontSize:11, color:t.mut, marginTop:2, fontFamily:F }}>Appear at top of home screen. Dismissible per device.</div>
        </div>
        <button onClick={()=>setShowNew(s=>!s)} style={{ padding:'7px 14px', borderRadius:8,
          background:showNew?'#e8edf2':'#314F5C', color:showNew?'#5a7a8a':'#E0F5FC',
          border:'none', fontFamily:F, fontSize:12, fontWeight:700, cursor:'pointer' }}>
          {showNew?'× Cancel':'+ New banner'}
        </button>
      </div>

      {showNew && (
        <div style={{ background:t.panel, borderRadius:14, padding:'18px 20px', border:`1px solid ${t.bd}`, marginBottom:14 }}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 120px', gap:10, marginBottom:10 }}>
            <div>
              <label style={{ fontSize:10, fontWeight:700, color:t.mut, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'.07em' }}>Title</label>
              <input value={draft.title} onChange={e=>setD('title',e.target.value)} placeholder='New clinics in Southeast Asia'
                style={{ width:'100%', border:'1px solid rgba(111,143,167,0.2)', borderRadius:8, padding:'8px 11px', fontSize:12.5, color:t.tx, background:t.rowbg, outline:'none', boxSizing:'border-box', fontFamily:F }} />
            </div>
            <div>
              <label style={{ fontSize:10, fontWeight:700, color:t.mut, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'.07em' }}>Type</label>
              <select value={draft.type} onChange={e=>setD('type',e.target.value)}
                style={{ width:'100%', border:'1px solid rgba(111,143,167,0.2)', borderRadius:8, padding:'8px 11px', fontSize:12.5, color:t.tx, background:t.rowbg, outline:'none', fontFamily:F }}>
                {BANNER_TYPES.map(t=><option key={t.id} value={t.id}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom:10 }}>
            <label style={{ fontSize:10, fontWeight:700, color:t.mut, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'.07em' }}>Body</label>
            <textarea value={draft.body} onChange={e=>setD('body',e.target.value)} placeholder="We've added 5,000 new clinics across Thailand, Malaysia and Cambodia."
              style={{ width:'100%', border:'1px solid rgba(111,143,167,0.2)', borderRadius:8, padding:'8px 11px', fontSize:12.5, color:t.tx, background:t.rowbg, outline:'none', minHeight:70, resize:'vertical', lineHeight:1.5, fontFamily:F, boxSizing:'border-box' }} />
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
            {['starts_at','ends_at'].map(k=>(
              <div key={k}>
                <label style={{ fontSize:10, fontWeight:700, color:t.mut, display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:'.07em' }}>
                  {k==='starts_at'?'Show from':'Hide after'} <span style={{ color:t.mut, fontWeight:400, textTransform:'none' }}>(optional)</span>
                </label>
                <input type='datetime-local' value={draft[k]} onChange={e=>setD(k,e.target.value)}
                  style={{ width:'100%', border:'1px solid rgba(111,143,167,0.2)', borderRadius:8, padding:'8px 11px', fontSize:12, color:t.tx, background:t.rowbg, outline:'none', fontFamily:F, boxSizing:'border-box' }} />
              </div>
            ))}
          </div>
          {(draft.title||draft.body)&&(()=>{
            const t=BANNER_TYPES.find(x=>x.id===draft.type)||BANNER_TYPES[0];
            return (<div style={{ marginBottom:12, padding:'8px 12px', borderRadius:10, background:t.bg, border:'1px solid '+t.border, display:'flex', alignItems:'flex-start', gap:10 }}>
              <div style={{ flex:1 }}>
                {draft.title&&<div style={{ fontSize:12, fontWeight:700, color:t.color, marginBottom:2 }}>{draft.title}</div>}
                {draft.body &&<div style={{ fontSize:11, color:t.color, lineHeight:1.5, opacity:.9 }}>{draft.body}</div>}
              </div>
              <span style={{ fontSize:14, color:t.color, opacity:.4 }}>×</span>
            </div>);
          })()}
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color:'#314F5C', fontFamily:F }}>
              <input type='checkbox' checked={alsoPush} onChange={e=>setAlsoPush(e.target.checked)} style={{ accentColor:'#314F5C', width:14, height:14 }} />
              Also send as push notification
            </label>
            <button onClick={create} disabled={!canCreate} style={{ padding:'8px 20px', borderRadius:9,
              background:canCreate?'#314F5C':'#e4edf3', color:canCreate?'#E0F5FC':'#9eb8c8',
              border:'none', fontFamily:F, fontSize:12, fontWeight:700, cursor:canCreate?'pointer':'default' }}>
              {saving?'Saving…':'Save Draft →'}
            </button>
          </div>
          {result&&<div style={{ marginTop:10, padding:'8px 12px', borderRadius:8, background:result.ok?'#eaf6ef':'#fef0ef', color:result.ok?'#2d7a4f':'#8b1c1c', fontSize:12, fontFamily:F }}>{result.msg}</div>}
        </div>
      )}

      <div style={{ background:t.panel, borderRadius:14, padding:'14px 16px', border:`1px solid ${t.bd}` }}>
        {loading
          ? <div style={{ fontSize:11, color:'#b0c8d4', fontFamily:F }}>Loading…</div>
          : banners.length===0
            ? <div style={{ fontSize:11.5, color:'#b0c8d4', fontStyle:'italic', textAlign:'center', padding:'16px 0', fontFamily:F }}>No banners yet — create one above</div>
            : banners.map(b=>{
                const t=BANNER_TYPES.find(x=>x.id===b.type)||BANNER_TYPES[0];
                return (
                  <div key={b.id} style={{ padding:'10px 0', borderBottom:'1px solid #f4f6f8', display:'flex', alignItems:'flex-start', gap:10 }}>
                    <div style={{ width:8, height:8, borderRadius:'50%', background:b.active?t.color:'#d0d8e0', flexShrink:0, marginTop:5 }} />
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:12, fontWeight:600, color:t.tx, fontFamily:F, marginBottom:2 }}>{b.title}</div>
                      <div style={{ fontSize:11, color:t.mut, fontFamily:F, lineHeight:1.4, marginBottom:4 }}>{b.body}</div>
                      <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
                        <span style={{ padding:'1px 7px', borderRadius:99, fontSize:9.5, fontWeight:700, background:t.bg, color:t.color, fontFamily:F }}>{t.label}</span>
                        {b.active?<span style={{ fontSize:9.5, color:'#1D9E75', fontWeight:700, fontFamily:F }}>● Live</span>
                                  :<span style={{ fontSize:9.5, color:'#b0c8d4', fontFamily:F }}>Inactive</span>}
                        {tfmt(b.starts_at)&&<span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>from {tfmt(b.starts_at)}</span>}
                        {tfmt(b.ends_at)  &&<span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>until {tfmt(b.ends_at)}</span>}
                      </div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexShrink:0, alignItems:'center' }}>
                      <button onClick={()=>toggle(b.id,!b.active)} style={{ fontSize:10, padding:'4px 10px', borderRadius:6, border:`1px solid ${t.bd}`, background:t.panel, color:'#314F5C', cursor:'pointer', fontFamily:F, fontWeight:600 }}>
                        {b.active?'Deactivate':'Activate'}
                      </button>
                      <button onClick={()=>remove(b.id)} style={{ fontSize:12, padding:'2px 6px', borderRadius:6, border:'none', background:'none', color:t.mut, cursor:'pointer', fontFamily:F }}>×</button>
                    </div>
                  </div>
                );
              })
        }
      </div>
    </div>
  );
}

// ── Blast tab ─────────────────────────────────────────────────────────────────
function BlastTab({ segments, isMobile, userEmail }) {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [selected, setSelected] = useState(['push','telegram']);
  const [message, setMessage] = useState('');
  const [mediaUrl,  setMediaUrl]  = useState('');
  const [mediaType, setMediaType] = useState(null); // 'image' | 'video' | null
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [segment, setSegment] = useState('all');
  const [schedule, setSchedule] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const recipSeg = segments.find(s => s.id===segment);
  const recipCount = recipSeg?.count||0;
  const canSend = message.trim().length>0 && selected.length>0;
  function toggle(ch) { setSelected(p => p.includes(ch) ? p.filter(c=>c!==ch) : [...p,ch]); }
  async function handleBlastMedia(file) {
    setUploadingMedia(true);
    try {
      const isVid = file.type.startsWith('video/');
      const url = isVid ? await uploadVideo(file) : await uploadImage(file);
      setMediaUrl(url);
      setMediaType(isVid ? 'video' : 'image');
    } catch (_e) { setResult({ ok:false, msg:'Upload failed: '+_e.message }); }
    setUploadingMedia(false);
  }

  async function doBlast() {
    setSending(true); setResult(null);
    let sent=0, failed=0;
    await Promise.all(selected.map(async ch => {
      try {
        if (ch==='email') { await supabase.from('email_campaigns').insert({ subject:message.trim().slice(0,80), body:message.trim(), segment, recipients:recipCount, status:schedule?'scheduled':'queued', ...(schedule?{scheduled_at:new Date(schedule).toISOString()}:{}), ...(mediaUrl?{image_url:mediaUrl}:{}) }); }
        else { const row = { platform:ch, content:message.trim(), status:schedule?'scheduled':'queued', created_by:(userEmail||'admin').split('@')[0], ...(mediaUrl?{media_url:mediaUrl}:{}) }; if (schedule) row.scheduled_at=schedule; await supabase.from('social_posts').insert(row); }
        sent++;
      } catch (_e) { failed++; }
    }));
    setResult({ ok:failed===0, msg:`Sent to ${sent} channel${sent!==1?'s':''}${failed>0?` (${failed} failed)`:''}.` });
    if (failed===0) { setMessage(''); setMediaUrl(''); setMediaType(null); setSchedule(''); }
    setSending(false); setConfirm(false);
  }

  return (
    <div>
      {confirm && <ConfirmModal channels={selected} recipientCount={recipCount} isTest={false} preview={message} onConfirm={doBlast} onCancel={() => setConfirm(false)} loading={sending} />}
      <div style={{ background:t.panel, borderRadius:14, padding:'20px', border:`1px solid ${t.bd}` }}>
        <div style={{ fontSize:14, fontWeight:700, color:t.tx, fontFamily:F, marginBottom:4 }}>Multi-channel Blast</div>
        <div style={{ fontSize:12, color:t.mut, fontFamily:F, marginBottom:18 }}>Write once. Send across all selected channels simultaneously.</div>
        <div style={{ marginBottom:16 }}>
          <label style={LBL(t)}>Channels</label>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {BLAST_CHANNELS.map(ch => { const active=selected.includes(ch); return (
              <button key={ch} onClick={() => toggle(ch)}
                style={{ display:'flex', alignItems:'center', gap:7, padding:'8px 14px', borderRadius:10, border:`1.5px solid ${active?CH[ch].color:'#dde8ed'}`, background: active?CH[ch].pale:'#fff', color: active?CH[ch].color:t.mut, cursor:'pointer', fontFamily:F, fontSize:12, fontWeight: active?700:500, transition:'all .12s' }}>
                <ChIcon ch={ch} size={13} color={active?CH[ch].color:t.mut} />
                {CH[ch].label}
              </button>
            ); })}
          </div>
        </div>
        <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap:12, marginBottom:12 }}>
          <div>
            <label style={LBL(t)}>Audience</label>
            <select style={{ ...inp(t), height:36 }} value={segment} onChange={e => setSegment(e.target.value)}>
              {segments.map(s => <option key={s.id} value={s.id}>{s.label} ({s.count.toLocaleString()})</option>)}
            </select>
          </div>
          <div>
            <div style={{ display:'flex', alignItems:'baseline', justifyContent:'space-between', marginBottom:5 }}>
              <label style={{ ...LBL(t), marginBottom:0 }}>Schedule <span style={{ color:t.mut, fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10 }}>(optional)</span></label>
              <span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>{USER_TZ}</span>
            </div>
            <input type='datetime-local' style={inp(t)} value={schedule} onChange={e => setSchedule(e.target.value)} />
          </div>
        </div>
        <div style={{ marginBottom:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between', marginBottom:5 }}>
            <label style={{ ...LBL(t), marginBottom:0 }}>Message</label>
            <span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>{message.length} chars</span>
          </div>
          <textarea style={{ ...inp(t), minHeight:110, resize:'vertical', lineHeight:1.6 }} value={message} onChange={e => setMessage(e.target.value)} placeholder={'Write a message that works across all selected channels…\n\nTip: Keep under 160 chars if SMS is selected.'} />
        </div>
        {message.length>0 && message.length<=160 && selected.includes('sms') && <div style={{ marginBottom:12, padding:'7px 12px', borderRadius:8, background:'#eaf6ef', fontSize:11, color:'#2d7a4f', fontFamily:F }}>\u2713 Fits in a single SMS ({message.length}/160)</div>}
        {message.length>160 && selected.includes('sms') && <div style={{ marginBottom:12, padding:'7px 12px', borderRadius:8, background:'#fff8e6', fontSize:11, color:'#7c4a0a', fontFamily:F }}>\u26a0\ufe0f Over 160 chars \u2014 will be split into {Math.ceil(message.length/160)} SMS messages</div>}
        {/* Media upload */}
        <div style={{ marginBottom:14 }}>
          <label style={LBL(t)}>Media <span style={{ color:t.mut, fontWeight:400, textTransform:'none', letterSpacing:0, fontSize:10 }}>(optional — image or video)</span></label>
          <MediaDropZone accept='image/*,video/*' onFile={f => handleBlastMedia(f)} uploading={uploadingMedia}
            url={mediaUrl} previewIsVideo={mediaType === 'video'}
            onClear={() => { setMediaUrl(''); setMediaType(null); }}
            hint='an image or video' />
          {!mediaUrl && (
            <input style={{ ...inp(t), marginTop:5, fontSize:11, color:t.mut }} value={mediaUrl}
              onChange={e => { setMediaUrl(e.target.value); setMediaType(e.target.value ? 'image' : null); }}
              placeholder='Or paste a media URL…' />
          )}
        </div>
        {result && <div style={{ padding:'8px 12px', borderRadius:8, background: result.ok?'#eaf6ef':'#fef0ef', color: result.ok?'#2d7a4f':'#8b1c1c', fontSize:12, fontFamily:F, marginBottom:12 }}>{result.msg}</div>}
        <div style={{ display:'flex', gap:8, justifyContent:'flex-end', alignItems:'center' }}>
          <div style={{ flex:1, display:'flex', gap:5 }}>
            {selected.map(ch => <div key={ch} style={{ width:22, height:22, borderRadius:6, background: ch==='instagram'?igGrad:CH[ch].color, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff' }}><ChIcon ch={ch} size={11} color='#fff' /></div>)}
          </div>
          <button onClick={() => setConfirm(true)} disabled={!canSend||sending}
            style={{ padding:'9px 22px', background: !canSend||sending?'#e4edf3':'#314F5C', color: !canSend||sending?'#9eb8c8':'#E0F5FC', border:'none', borderRadius:9, fontSize:12, fontWeight:700, cursor: canSend&&!sending?'pointer':'default', fontFamily:F }}>
            {sending ? 'Sending…' : `Blast to ${selected.length} channel${selected.length!==1?'s':''} →`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Queue tab ─────────────────────────────────────────────────────────────────
const PLAT_COLORS = { email:'#314F5C', push:'#5856d6', sms:'#1a9c3e', telegram:'#229ED9', instagram:'#E1306C', tiktok:'#010101', pinterest:'#e60023' };
const Q_STATUS = { scheduled:{bg:'#e0f5fc',c:'#185FA5'}, sent:{bg:'#eaf6ef',c:'#2d7a4f'}, queued:{bg:'#fff8e6',c:'#9f6c00'}, failed:{bg:'#fef0ef',c:'#8b1c1c'}, cancelled:{bg:'#f4f6f8',c:'#9eb8c8'}, sending:{bg:'#f0eeff',c:'#5340b5'} };

function QueueTab() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data:social }, { data:email }] = await Promise.all([
        supabase.from('social_posts').select('id,platform,content,scheduled_at,status,created_by,created_at').order('created_at',{ascending:false}).limit(80),
        supabase.from('email_campaigns').select('id,subject,body,image_url,scheduled_at,segment,recipients,sent,failed,open_count,click_count,status,created_at').order('created_at',{ascending:false}).limit(30),
      ]);
      const merged = [
        ...(social||[]).map(r => ({ ...r, _type:'social' })),
        ...(email||[]).map(r => ({ ...r, platform:'email', content:r.subject, _type:'email' })),
      ].sort((a,b) => new Date(b.created_at)-new Date(a.created_at));
      setPosts(merged);
    } catch (_e) {}
    setLoading(false);
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  async function handleCancel(post) {
    const tbl = post._type==='email'?'email_campaigns':'social_posts';
    await supabase.from(tbl).update({status:'cancelled'}).eq('id',post.id);
    setPosts(prev=>prev.map(p=>p.id===post.id?{...p,status:'cancelled'}:p));
  }
  async function handleRetry(post) {
    const tbl = post._type==='email'?'email_campaigns':'social_posts';
    await supabase.from(tbl).update({status:'queued'}).eq('id',post.id);
    setPosts(prev=>prev.map(p=>p.id===post.id?{...p,status:'queued'}:p));
  }

  const statuses = ['all',...new Set(posts.map(p=>p.status).filter(Boolean))];
  const filtered = posts.filter(p => {
    if (filter!=='all' && p.status!==filter) return false;
    if (search && !(p.content||p.subject||'').toLowerCase().includes(search.toLowerCase())) return false;
    if (dateFrom && new Date(p.created_at)<new Date(dateFrom)) return false;
    if (dateTo   && new Date(p.created_at)>new Date(dateTo+'T23:59:59')) return false;
    return true;
  });
  const counts = { queued:posts.filter(p=>p.status==='queued').length, scheduled:posts.filter(p=>p.status==='scheduled').length, sent:posts.filter(p=>p.status==='sent').length };

  return (
    <div>
      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:16 }}>
        {[{k:'queued',l:'Queued',c:'#9f6c00',b:'#fff8e6'},{k:'scheduled',l:'Scheduled',c:'#185FA5',b:'#e0f5fc'},{k:'sent',l:'Sent',c:'#0d6640',b:'#eaf7f2'}].map(({ k,l,c,b }) => (
          <div key={k} style={{ background:t.panel, borderRadius:11, padding:'12px 14px', border:`1px solid ${t.bd}` }}>
            <div style={{ fontSize:24, fontWeight:300, color:'#0f1e28', lineHeight:1, marginBottom:5, fontFamily:F }}>{loading?'…':counts[k]}</div>
            <span style={{ padding:'2px 8px', borderRadius:99, fontSize:10, fontWeight:700, background:b, color:c, fontFamily:F }}>{l}</span>
          </div>
        ))}
      </div>
      <div style={{ background:t.panel, borderRadius:14, padding:'16px 18px', boxShadow:'0 1px 4px rgba(15,30,40,0.07)' }}>
        <div style={{ display:'flex', gap:8, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
          <div style={{ position:'relative', flex:'1 1 160px' }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9eb8c8" strokeWidth="2.2" strokeLinecap="round" style={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', pointerEvents:'none' }}><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder='Search campaigns…' style={{ ...inp(t), paddingLeft:28, fontSize:12 }} />
          </div>
          <input type='date' value={dateFrom} onChange={e => setDateFrom(e.target.value)} style={{ ...inp(t), width:130, fontSize:12 }} title='From date' />
          <input type='date' value={dateTo}   onChange={e => setDateTo(e.target.value)}   style={{ ...inp(t), width:130, fontSize:12 }} title='To date' />
          {(search||dateFrom||dateTo) && <button onClick={() => { setSearch(''); setDateFrom(''); setDateTo(''); }} style={{ padding:'7px 11px', background:'transparent', border:`1px solid ${t.bd}`, borderRadius:8, fontSize:11, color:t.mut, cursor:'pointer', fontFamily:F }}>Clear</button>}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14, flexWrap:'wrap' }}>
          <span style={{ fontSize:13, fontWeight:700, color:t.tx, fontFamily:F, flex:1 }}>All campaigns <span style={{ color:t.mut, fontWeight:400, fontSize:12 }}>({filtered.length})</span></span>
          <div style={{ display:'flex', gap:5 }}>
            {statuses.map(s => (
              <button key={s} onClick={() => setFilter(s)} style={{ fontSize:10.5, padding:'4px 10px', borderRadius:99, border: filter===s ? '1.5px solid #314F5C' : `1px solid ${t.bd}`, background: filter===s ? '#314F5C' : '#fff', color: filter===s ? '#fff' : '#6F8FA7', cursor:'pointer', fontFamily:F, fontWeight: filter===s ? 700 : 400 }}>
                {s==='all' ? 'All' : s.charAt(0).toUpperCase()+s.slice(1)}
              </button>
            ))}
          </div>
          <button onClick={loadAll} style={{ padding:'4px 10px', background:'transparent', border:`1px solid ${t.bd}`, borderRadius:8, fontSize:11, color:t.mut, cursor:'pointer', fontFamily:F }}>↻</button>
        </div>
        {loading ? [1,2,3].map(i => <div key={i} style={{ height:48, borderRadius:8, marginBottom:7, background:'#edf2f5' }} />) : (
          filtered.length===0 ? (
            <div style={{ textAlign:'center', padding:'28px 0', color:t.mut, fontSize:12.5, fontFamily:F }}>
              {search||dateFrom||dateTo ? 'No campaigns match your filters' : 'No campaigns yet'}
            </div>
          ) : filtered.map((post, idx) => {
            const platColor = PLAT_COLORS[post.platform?.toLowerCase()]||'#6F8FA7';
            const ss = Q_STATUS[post.status]||Q_STATUS.queued;
            const canCancel = post.status==='scheduled'||post.status==='queued';
            const canRetry  = post.status==='failed'||post.status==='cancelled';
            const hasSent   = (post.sent||0)>0;
            const openPct   = hasSent && post.open_count  ? pct(post.open_count,  post.sent) : null;
            const clickPct  = hasSent && post.click_count ? pct(post.click_count, post.sent) : null;
            return (
              <div key={post.id||idx} style={{ display:'flex', alignItems:'center', gap:10, padding:'9px 0', borderBottom:'1px solid #f4f6f8' }}>
                <div style={{ width:30, height:30, borderRadius:8, background: post.platform==='instagram'?igGrad:platColor, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, color:'#fff' }}>
                  <ChIcon ch={post.platform||'email'} size={13} color='#fff' />
                </div>
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:2 }}>
                    <span style={{ fontSize:12, fontWeight:500, color:t.tx, fontFamily:F, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', flex:1 }}>{post.content||post.subject||'—'}</span>
                    <span style={{ padding:'1px 6px', borderRadius:99, fontSize:9.5, fontWeight:700, background:ss.bg, color:ss.c, fontFamily:F, flexShrink:0 }}>{post.status}</span>
                  </div>
                  <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                    <span style={{ fontSize:9.5, color:t.mut, fontFamily:F }}>{timeAgo(post.created_at)}</span>
                    {hasSent && <span style={{ fontSize:9.5, color:'#17a86a', fontFamily:F }}>✓{post.sent}</span>}
                    {openPct  && <span style={{ fontSize:9.5, color:'#2563eb', fontFamily:F }}>👁 {openPct}</span>}
                    {clickPct && <span style={{ fontSize:9.5, color:'#9F7E4F', fontFamily:F }}>↗ {clickPct}</span>}
                    {post.failed>0 && <span style={{ fontSize:9.5, color:'#c62828', fontFamily:F }}>✗{post.failed}</span>}
                    <div style={{ flex:1 }} />
                    {canCancel && <button onClick={() => handleCancel(post)} style={{ fontSize:9.5, color:'#c62828', background:'none', border:'none', cursor:'pointer', fontFamily:F, fontWeight:700, padding:0 }}>Cancel</button>}
                    {canRetry  && <button onClick={() => handleRetry(post)}  style={{ fontSize:9.5, color:'#314F5C', background:'none', border:'none', cursor:'pointer', fontFamily:F, fontWeight:700, padding:0 }}>Retry</button>}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────
const _ALL_TABS = [...CHANNEL_ORDER, 'blast', 'queue'];

function CampaignsHubBody() {
  const { t } = useConsoleTheme(); // eslint-disable-line no-unused-vars
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('email');
  const [segments, setSegments] = useState([
    { id:'all',      label:'All pass holders',   count:0 },
    { id:'active',   label:'Active passes',       count:0 },
    { id:'expiring', label:'Expiring in 30 days', count:0 },
    { id:'waitlist', label:'Waitlist',             count:0 },
  ]);
  const [segLoading, setSegLoading] = useState(true);
  const [userEmail, setUserEmail]   = useState('');
  // Per-channel sent counts for channel strip
  const [channelStats, setChannelStats] = useState({});

  useEffect(() => {
    (async () => {
      try {
        // Counts must run as SECURITY DEFINER — the user_passes RLS policy
        // "Users can read own passes" restricts SELECT to auth.uid()=user_id,
        // so a direct count from the admin dashboard returns 0 for every
        // segment. get_admin_segment_counts() checks _is_admin_user() server-
        // side and returns real counts only for the admin allowlist.
        const { data: rpcRow } = await supabase.rpc('get_admin_segment_counts');
        const row = Array.isArray(rpcRow) ? rpcRow[0] : rpcRow;
        const allP    = row?.all_passes       || 0;
        const active  = row?.active_passes    || 0;
        const expiring= row?.expiring_30d     || 0;
        const waitlist= row?.waitlist_waiting || 0;
        setSegments([
          { id:'all',      label:'All pass holders',   count:allP },
          { id:'active',   label:'Active passes',       count:active },
          { id:'expiring', label:'Expiring in 30 days', count:expiring },
          { id:'waitlist', label:'Waitlist',             count:waitlist },
        ]);
      } catch (_e) {}
      setSegLoading(false);
    })();
    supabase.auth.getUser().then(({ data }) => { if (data?.user?.email) setUserEmail(data.user.email); });
    // Load channel stats for strip
    (async () => {
      try {
        const { data:emailData } = await supabase.from('email_campaigns').select('sent,recipients').not('status','eq','cancelled');
        const { data:socialData } = await supabase.from('social_posts').select('platform,status');
        const stats = {};
        if (emailData) { const total=emailData.reduce((a,r)=>a+(r.sent||r.recipients||0),0); if(total>0) stats.email=total+' sent'; }
        if (socialData) {
          const byCh = {};
          socialData.forEach(r => { if (!byCh[r.platform]) byCh[r.platform]=0; byCh[r.platform]++; });
          Object.entries(byCh).forEach(([k,v]) => { stats[k]=v+' posts'; });
        }
        setChannelStats(stats);
      } catch (_e) {}
    })();
  }, []); // eslint-disable-line

  const tabLabel = { email:'Email', push:'Push', sms:'SMS', telegram:'Telegram', instagram:'Instagram', tiktok:'TikTok', pinterest:'Pinterest', blast:'Blast', queue:'Queue' };

  return (
    <div style={{ fontFamily:F }}>
      {/* ── Channel strip ── */}
      <div style={{ display:'flex', gap:6, marginBottom:20, overflowX:'auto', paddingBottom:2 }}>
        {CHANNEL_ORDER.map(ch => (
          <ChannelCard key={ch} ch={ch} active={tab===ch} stat={channelStats[ch]||null} onClick={() => setTab(ch)} />
        ))}
        {/* Blast */}
        <button onClick={() => setTab('blast')}
          style={{ background: tab==='blast' ? '#314F5C' : '#fff', border:'none', borderRadius:11, padding:'10px 6px 9px', cursor:'pointer', textAlign:'center', transition:'all .15s', boxShadow: tab==='blast' ? '0 4px 14px rgba(49,79,92,.3), 0 0 0 1.5px #314F5C' : '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.06)', fontFamily:F, flex:1 }}>
          <div style={{ width:28, height:28, borderRadius:8, background: tab==='blast'?'rgba(255,255,255,.15)':'#E0F5FC', margin:'0 auto 5px', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <ChIcon ch='blast' size={14} color={tab==='blast'?'#fff':'#314F5C'} />
          </div>
          <div style={{ fontSize:9.5, fontWeight:700, color: tab==='blast'?'#fff':'#314F5C', letterSpacing:'.02em' }}>Blast</div>
          <div style={{ fontSize:8.5, color: tab==='blast'?'rgba(255,255,255,.65)':'#9eb8c8', marginTop:1 }}>multi</div>
        </button>
        {/* Queue */}
        <button onClick={() => setTab('queue')}
          style={{ background: tab==='queue' ? '#6F8FA7' : '#fff', border:'none', borderRadius:11, padding:'10px 6px 9px', cursor:'pointer', textAlign:'center', transition:'all .15s', boxShadow: tab==='queue' ? '0 4px 14px rgba(111,143,167,.35), 0 0 0 1.5px #6F8FA7' : '0 1px 3px rgba(15,30,40,0.07), 0 0 0 1px rgba(15,30,40,0.06)', fontFamily:F, flex:1 }}>
          <div style={{ width:28, height:28, borderRadius:8, background: tab==='queue'?'rgba(255,255,255,.15)':'rgba(111,143,167,0.12)', margin:'0 auto 5px', display:'flex', alignItems:'center', justifyContent:'center' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={tab==='queue'?'#fff':'#6F8FA7'} strokeWidth="2" strokeLinecap="round"><line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/></svg>
          </div>
          <div style={{ fontSize:9.5, fontWeight:700, color: tab==='queue'?'#fff':'#6F8FA7', letterSpacing:'.02em' }}>Queue</div>
          <div style={{ fontSize:8.5, color: tab==='queue'?'rgba(255,255,255,.65)':'#9eb8c8', marginTop:1 }}>history</div>
        </button>
      </div>

      {/* ── Screen label ── */}
      {tab!=='queue' && tab!=='blast' && (
        <div style={{ fontSize:11, color:t.mut, fontFamily:F, marginBottom:14, display:'flex', alignItems:'center', gap:6 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background: CH[tab]?.color||'#314F5C' }} />
          {tabLabel[tab]} channel
        </div>
      )}

      {tab==='email'     && <EmailTab     segments={segments} segLoading={segLoading} isMobile={isMobile} userEmail={userEmail} onHistoryChange={null} />}
      {tab==='push'      && <ChannelTab   channel='push'      segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='sms'       && <ChannelTab   channel='sms'       segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='telegram'  && <ChannelTab   channel='telegram'  segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='instagram' && <ChannelTab   channel='instagram' segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='tiktok'    && <ChannelTab   channel='tiktok'    segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='pinterest' && <ChannelTab   channel='pinterest' segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='blast'     && <BlastTab     segments={segments} isMobile={isMobile} userEmail={userEmail} />}
      {tab==='queue'     && <QueueTab />}
    </div>
  );
}

export default function CampaignsHub(props) {
  if (props && props.embed) return <CampaignsHubBody />;
  return <ConsoleShell title="Campaigns"><CampaignsHubBody /></ConsoleShell>;
}
